import { ScheduleInterval } from '../hooks/useScheduleIntervals';

export interface ConstraintViolation {
  type: 'overlap' | 'max_hours' | 'min_break' | 'meal_required';
  severity: 'error' | 'warning';
  message: string;
}

/**
 * Check if a new interval would overlap with existing intervals for the same agent
 */
export function checkOverlap(
  newInterval: { intervalStart: number; intervalEnd: number; agentId: string },
  existingIntervals: ScheduleInterval[]
): ConstraintViolation | null {
  const agentIntervals = existingIntervals.filter(
    (interval) => interval.agentId === newInterval.agentId
  );

  for (const existing of agentIntervals) {
    // Check if intervals overlap
    const overlaps =
      newInterval.intervalStart <= existing.intervalEnd &&
      newInterval.intervalEnd >= existing.intervalStart;

    if (overlaps) {
      return {
        type: 'overlap',
        severity: 'error',
        message: `This block overlaps with an existing ${existing.activityType} block`,
      };
    }
  }

  return null;
}

/**
 * Check if total scheduled hours exceed daily maximum (typically 12 hours)
 */
export function checkMaxHours(
  newInterval: { intervalStart: number; intervalEnd: number; agentId: string },
  existingIntervals: ScheduleInterval[],
  maxHours: number = 12
): ConstraintViolation | null {
  const agentIntervals = existingIntervals.filter(
    (interval) => interval.agentId === newInterval.agentId
  );

  // Calculate total intervals (including new one)
  const totalIntervals =
    agentIntervals.reduce(
      (sum, interval) => sum + (interval.intervalEnd - interval.intervalStart + 1),
      0
    ) +
    (newInterval.intervalEnd - newInterval.intervalStart + 1);

  // Convert intervals to hours (4 intervals = 1 hour)
  const totalHours = totalIntervals / 4;

  if (totalHours > maxHours) {
    return {
      type: 'max_hours',
      severity: 'warning',
      message: `Total scheduled hours (${totalHours.toFixed(1)}h) exceeds maximum (${maxHours}h)`,
    };
  }

  return null;
}

/**
 * Check if agent has required meal break for shifts > 6 hours
 */
export function checkMealBreak(
  agentId: string,
  existingIntervals: ScheduleInterval[]
): ConstraintViolation | null {
  const agentIntervals = existingIntervals.filter(
    (interval) => interval.agentId === agentId
  );

  // Calculate total work hours (excluding breaks/meals)
  const workIntervals = agentIntervals.filter(
    (interval) => !['MEAL', 'BREAK'].includes(interval.activityType)
  );
  const totalWorkIntervals = workIntervals.reduce(
    (sum, interval) => sum + (interval.intervalEnd - interval.intervalStart + 1),
    0
  );
  const workHours = totalWorkIntervals / 4;

  // Check if has meal break
  const hasMealBreak = agentIntervals.some(
    (interval) => interval.activityType === 'MEAL'
  );

  if (workHours > 6 && !hasMealBreak) {
    return {
      type: 'meal_required',
      severity: 'warning',
      message: `Agent works ${workHours.toFixed(1)}h but has no meal break (required for shifts > 6h)`,
    };
  }

  return null;
}

/**
 * Check if agent has minimum break time (typically 15 min every 4 hours)
 */
export function checkMinBreaks(
  agentId: string,
  existingIntervals: ScheduleInterval[]
): ConstraintViolation | null {
  const agentIntervals = existingIntervals.filter(
    (interval) => interval.agentId === agentId
  );

  // Calculate total work hours
  const workIntervals = agentIntervals.filter(
    (interval) => !['MEAL', 'BREAK'].includes(interval.activityType)
  );
  const totalWorkIntervals = workIntervals.reduce(
    (sum, interval) => sum + (interval.intervalEnd - interval.intervalStart + 1),
    0
  );
  const workHours = totalWorkIntervals / 4;

  // Count breaks
  const breakIntervals = agentIntervals.filter(
    (interval) => interval.activityType === 'BREAK'
  );

  // Rough heuristic: 1 break per 4 hours
  const requiredBreaks = Math.floor(workHours / 4);
  const actualBreaks = breakIntervals.length;

  if (workHours > 4 && actualBreaks < requiredBreaks) {
    return {
      type: 'min_break',
      severity: 'warning',
      message: `Agent works ${workHours.toFixed(1)}h but only has ${actualBreaks} break(s) (recommended: ${requiredBreaks})`,
    };
  }

  return null;
}

/**
 * Validate all constraints for a new interval
 */
export function validateInterval(
  newInterval: { intervalStart: number; intervalEnd: number; agentId: string; id?: string },
  existingIntervals: ScheduleInterval[]
): ConstraintViolation[] {
  const violations: ConstraintViolation[] = [];

  // Filter out the interval being edited (if updating)
  const filteredIntervals = newInterval.id
    ? existingIntervals.filter((interval) => interval.id !== newInterval.id)
    : existingIntervals;

  // Check overlap (hard constraint - error)
  const overlapViolation = checkOverlap(newInterval, filteredIntervals);
  if (overlapViolation) {
    violations.push(overlapViolation);
  }

  // Check max hours (soft constraint - warning)
  const maxHoursViolation = checkMaxHours(newInterval, filteredIntervals);
  if (maxHoursViolation) {
    violations.push(maxHoursViolation);
  }

  // Check meal break (soft constraint - warning)
  // Only check after adding the new interval
  const allIntervals = [...filteredIntervals, newInterval as any];
  const mealViolation = checkMealBreak(newInterval.agentId, allIntervals);
  if (mealViolation) {
    violations.push(mealViolation);
  }

  return violations;
}
